import React from 'react';
import { BaseScanGridWidget } from './BaseScanGridWidget';
import { 
    extractInfrastructureFindings, 
    DEFAULT_INFRASTRUCTURE_CATEGORIES,
    evaluateDataSourceConfig 
} from './KeyFindingsAdvancedWidgetHelper';
import { 
    extractManagedServiceFindings, 
    DEFAULT_MANAGED_SERVICE_CATEGORIES,
    getManagedServiceStatusDescription
} from './ManagedServiceExperienceWidgetHelper';
import { 
    extractSecurityFindings, 
    DEFAULT_SECURITY_CATEGORIES,
    getSecurityStatusDescription
} from './SecurityScanGridWidgetHelper';
import { 
    extractCyberSecurityFindings, 
    DEFAULT_CYBER_SECURITY_CATEGORIES,
    getCyberSecurityStatusDescription
} from './CyberSecurityGridWidgetHelper';
import { 
    getInfrastructureStatusDescription 
} from './KeyFindingsAdvancedWidgetHelper';

/**
 * CombinedScanGridWidget
 * A single widget that can be configured to show any of the 4 scan grid types.
 */
export const CombinedScanGridWidget = ({ data, config = {}, style = {}, onConfigChange, theme = {}, scanGroups = [], availableCategories = [] }) => {
    const { gridType = 'infrastructure', title: configTitle, dataOverrides = {} } = config;

    // 1. Determine which logic to use based on gridType
    let extractFn;
    let defaultCategories = [];
    let defaultTitle = 'SCAN GRID';
    let categoryName = 'Auto';

    // Bridge legacy keys to backend scan_type keys
    const legacyMap = {
        'infrastructure': 'Infrastructure',
        'managed-service': 'Managed IT/Service Experience',
        'security': 'Security',
        'cyber-security': 'Cyber Security (Threat & Exposure)',
        'network-scan': 'Network Scan',
        'localhost-scan': 'Localhost Scan',
        'ad-scan': 'AD Scan',
        'ms365-scan': 'MS365 Scan',
        'pii-scan': 'PII Scan'
    };

    const backendKey = legacyMap[gridType] || gridType;
    const group = scanGroups.find(g => g.key === backendKey);

    if (group) {
        defaultTitle = group.label.toUpperCase();
        categoryName = group.label;
        // Resolve dynamic defaults from availableCategories
        defaultCategories = availableCategories
            .filter(cat => cat.scan_type === group.key)
            .map(cat => ({ id: cat.name, label: cat.name }));
    }

    // Special specialized logic matching (override if needed)
    switch (gridType) {
        case 'managed-service':
            extractFn = extractManagedServiceFindings;
            defaultCategories = DEFAULT_MANAGED_SERVICE_CATEGORIES;
            break;
        case 'security':
            extractFn = extractSecurityFindings;
            defaultCategories = DEFAULT_SECURITY_CATEGORIES;
            break;
        case 'cyber-security':
            extractFn = extractCyberSecurityFindings;
            defaultCategories = DEFAULT_CYBER_SECURITY_CATEGORIES;
            break;
        case 'infrastructure':
            extractFn = extractInfrastructureFindings;
            defaultCategories = DEFAULT_INFRASTRUCTURE_CATEGORIES;
            break;
        default:
            const { matchesScanType } = require('./FindingsWidgets');
            extractFn = (d) => {
                const { recreateFindingsArray } = require('./FindingsWidgets');
                const allFindings = recreateFindingsArray(d);
                return allFindings
                    .filter(f => matchesScanType(f, backendKey))
                    .map(f => ({
                        id: f[0],
                        title: f[0],
                        status: f[1], // Will be normalized later
                        score: f[2],
                        description: f[3],
                        metrics: f[5] ? Object.entries(f[5]).map(([label, value]) => ({ label, value })) : [],
                        category: f[6] || backendKey
                    }));
            };
            break;
    }

    const title = configTitle || defaultTitle;

    // 2. Extract base findings
    const baseFindings = extractFn(data, config);
    const baseFindingsMap = baseFindings.reduce((acc, f) => {
        acc[f.id] = f;
        if (f.title && f.title !== f.id) acc[f.title] = f; // also index by display label
        return acc;
    }, {});

    // 2b. Aggregate ALL base findings from all specialized extractors as a global fallback
    const globalBaseFindingsMap = (() => {
        const { recreateFindingsArray, matchesScanType } = require('./FindingsWidgets');
        
        // Helper to extract findings for standard scan types
        const extractStandard = (d, scanType) => {
            const allFindings = recreateFindingsArray(d);
            return allFindings
                .filter(f => matchesScanType(f, scanType))
                .map(f => ({
                    id: f[0],
                    title: f[0],
                    status: f[1],
                    score: f[2],
                    description: f[3],
                    metrics: f[5] ? Object.entries(f[5]).map(([label, value]) => ({ label, value })) : [],
                    category: f[6] || scanType
                }));
        };

        const all = [
            ...extractManagedServiceFindings(data, config),
            ...extractSecurityFindings(data, config),
            ...extractCyberSecurityFindings(data, config),
            ...extractInfrastructureFindings(data, config),
            ...(scanGroups || []).map(g => extractStandard(data, g.label)).flat()
        ];
        return all.reduce((acc, f) => {
            acc[f.id] = f;           // keyed by internal ID (e.g. 'cyber-security-1')
            if (f.title && f.title !== f.id) acc[f.title] = f; // also keyed by display label
            return acc;
        }, {});
    })();

    // 3. Define the discovery pool (all findings from all types)
    const { recreateFindingsArray, SCAN_TYPE_MAPPING, MANDATORY_CATEGORIES, matchesScanType } = require('./FindingsWidgets');
    const discoveryPool = recreateFindingsArray(data);
    const discoveryMap = discoveryPool.reduce((acc, f) => {
        const [name, rawStatus, score, description, , metrics, explicitScanType] = f;
        
        // Helper to normalize status for BaseScanGridWidget (Critical -> Red, etc.)
        const normalizeStatus = (rs) => {
            if (!rs) return 'Unknown';
            if (rs === 'Red' || rs === 'Yellow' || rs === 'Green' || rs === 'Unknown') return rs;
            if (rs === 'Critical' || rs === 'Bad') return 'Red';
            if (rs === 'Warning' || rs === 'Moderate') return 'Yellow';
            if (rs === 'Healthy' || rs === 'Good' || rs === 'Pass') return 'Green';
            return 'Unknown';
        };

        const status = normalizeStatus(rawStatus);
        const scanType = explicitScanType || SCAN_TYPE_MAPPING[name] || 'Other';

        acc[name] = {
            id: name,
            title: name,
            status,
            score,
            description,
            metrics: metrics ? Object.entries(metrics).map(([label, value]) => ({ label, value })) : [],
            category: scanType
        };
        return acc;
    }, {});

    // Helper for manual cycling of status descriptions
    const getStatusDescription = (finding, status) => {
        if (gridType === 'infrastructure') return getInfrastructureStatusDescription(finding.id, status) || finding.description;
        if (gridType === 'managed-service') return getManagedServiceStatusDescription(finding.id, status) || finding.description;
        if (gridType === 'security') return getSecurityStatusDescription(finding.id, status) || finding.description;
        if (gridType === 'cyber-security') return getCyberSecurityStatusDescription(finding.id, status) || finding.description;
        
        return finding.description;
    };

    // 4. Handle card click for manual override
    const handleCardClick = (finding) => {
        if (!onConfigChange) return;

        const statusCycle = ['Green', 'Yellow', 'Red', 'Unknown'];
        const currentStatus = finding.status || 'Unknown';
        const nextStatus = statusCycle[(statusCycle.indexOf(currentStatus) + 1) % statusCycle.length];
        
        let nextScore = 0;
        if (nextStatus === 'Green') nextScore = 100;
        else if (nextStatus === 'Yellow') nextScore = 50;
        
        onConfigChange({
            ...config,
            dataOverrides: {
                ...dataOverrides,
                [finding.id]: {
                    status: nextStatus,
                    score: nextScore,
                    description: getStatusDescription(finding, nextStatus),
                    mode: 'manual'
                }
            }
        });
    };

    // 5. Build final findings list
    // 5. Build final findings list
    const categories = config?.categories || defaultCategories;
    const allFinalFindings = categories.map(cat => {
        const override = dataOverrides[cat.id] || {};
        const mode = override.mode || 'auto';
        
        // Resolve the "source" finding logic
        let sourceFinding = null;
        if (mode === 'auto') {
            const explicitId = override.autoSourceId;
            const explicitType = override.autoSourceType;

            // Helper to resolve finding with fuzzy matching
            const resolveFinding = (id, label, type) => {
                if (!id) return null;
                
                // 1. Exact match in any of our maps (by ID)
                let found = discoveryMap[id] || baseFindingsMap[id] || globalBaseFindingsMap[id];
                if (found) return found;

                // 1b. Exact match by Label (Smart Fallback)
                if (label) {
                    found = discoveryMap[label] || baseFindingsMap[label] || globalBaseFindingsMap[label];
                    if (found) return found;
                }

                // 2. Fuzzy match for standard categories
                if (type) {
                    const fuzzyMatch = Object.values(discoveryMap).find(f => 
                        matchesScanType(f, type) && (
                            f.title === id || f.title.startsWith(`${id} `) || 
                            (label && (f.title === label || f.title.startsWith(`${label} `)))
                        )
                    ) || Object.values(globalBaseFindingsMap).find(f =>
                        matchesScanType(f, type) && (
                            f.title === id || f.title.startsWith(`${id} `) ||
                            (label && (f.title === label || f.title.startsWith(`${label} `)))
                        )
                    );
                    if (fuzzyMatch) return fuzzyMatch;
                }

                // 3. Stub matching
                const lookupId = id || label;
                if (lookupId && MANDATORY_CATEGORIES.includes(lookupId)) {
                    return {
                        id: lookupId,
                        title: lookupId,
                        status: 'Unknown',
                        score: 0,
                        description: 'No data available for this scan category.',
                        metrics: [],
                        category: type || SCAN_TYPE_MAPPING[lookupId] || 'Scan Result'
                    };
                }
                
                return null;
            };

            if (explicitId && explicitType) {
                sourceFinding = resolveFinding(explicitId, cat.label, explicitType);
            } else if (explicitId) {
                sourceFinding = resolveFinding(explicitId, cat.label, null);
            } else {
                sourceFinding = baseFindingsMap[cat.id] || globalBaseFindingsMap[cat.id] || (cat.label ? (baseFindingsMap[cat.label] || globalBaseFindingsMap[cat.label]) : null);
                if (!sourceFinding) {
                    sourceFinding = resolveFinding(cat.id, cat.label, null);
                }
            }
        }

        const baseFinding = {
            id: cat.id,
            title: sourceFinding ? sourceFinding.title : (cat.label || 'New Item'),
            originalTitle: sourceFinding ? sourceFinding.title : (cat.label || 'New Item'),
            status: sourceFinding ? sourceFinding.status : 'Unknown',
            score: sourceFinding ? sourceFinding.score : 0,
            description: sourceFinding ? sourceFinding.description : 'Configure this item',
            metrics: sourceFinding ? sourceFinding.metrics : [],
            category: categoryName
        };

        let finalFinding = { ...baseFinding, isManual: false };

        if (override && (override.status || override.mode === 'manual')) {
            const statusToScore = { 'Green': 100, 'Yellow': 50, 'Red': 0, 'Unknown': 0 };
            finalFinding = {
                ...baseFinding,
                status: override.status,
                score: override.score !== undefined ? override.score : (statusToScore[override.status] || 0),
                description: override.description || getStatusDescription(baseFinding, override.status),
                isManual: true
            };
        } else if (override && override.mode === 'dataSource') {
            const result = evaluateDataSourceConfig(data, override);
            finalFinding = {
                ...baseFinding,
                status: result.status,
                score: result.score,
                description: result.description,
                metrics: result.metrics.length > 0 ? result.metrics : baseFinding.metrics,
                isDataSource: true
            };
        }

        // Variable replacement helper for notes
        const applyVariables = (noteTemplate, finding) => {
            if (!noteTemplate) return noteTemplate;
            let note = noteTemplate;
            
            // {value} -> score
            note = note.replace(/{value}/g, Math.round(finding.score));
            
            // Map metrics ({count}, {total}, {percent})
            if (finding.metrics && finding.metrics.length > 0) {
                finding.metrics.forEach(m => {
                    const label = (m.label || '').toLowerCase();
                    if (label === 'count' || label === 'supported' || label === 'active') {
                        note = note.replace(/{count}/g, m.value);
                    } else if (label === 'total') {
                        note = note.replace(/{total}/g, m.value);
                    } else if (label === 'percentage' || label === 'percent') {
                        note = note.replace(/{percent}/g, m.value);
                    }
                    // Direct dynamic replacement for specified label name
                    note = note.replace(new RegExp(`{${label}}`, 'g'), m.value);
                });
            }
            return note;
        };

        // Apply Threshold Note Customization
        const customNotes = override?.customNotes || {};
        const currentStatus = finalFinding.status; // Red, Yellow, Green, Unknown
        const tierMap = { 'Green': 'Good', 'Yellow': 'Moderate', 'Red': 'Bad' };

        // Map normalized status back to Critical/Warning/Healthy keys used in Properties Panel
        const statusToNoteKey = { 'Red': 'Critical', 'Yellow': 'Warning', 'Green': 'Healthy' };
        const noteKey = statusToNoteKey[currentStatus] || currentStatus;
        
        let finalDescription = finalFinding.description;

        // 1. Check for manual/template override from properties panel
        if (customNotes[noteKey]) {
            finalDescription = applyVariables(customNotes[noteKey], finalFinding);
        } 
        // 2. Fallback to legacy single description
        else if (override?.description && (!override.mode || override.mode === 'auto')) {
            finalDescription = override.description;
        }
        // 3. Automated Fallback: Try industry-standard notes from system thresholds
        else if (data?.scanData?.category_thresholds || data?.category_thresholds) {
            const thresholds = data?.scanData?.category_thresholds || data?.category_thresholds;
            const catName = finalFinding.originalTitle || finalFinding.title;
            const lowerCatName = catName?.trim().toLowerCase();

            // Robust lookup (Exact -> Case-insensitive -> Partial)
            let threshold = thresholds[catName];
            if (!threshold) {
                const match = Object.entries(thresholds).find(([k]) => {
                    const key = k.trim().toLowerCase();
                    return key === lowerCatName || key.includes(lowerCatName) || lowerCatName.includes(key);
                });
                if (match) threshold = match[1];
            }

            if (threshold) {
                const tier = tierMap[currentStatus];
                // Try both 'Healthy' and 'Good' if status is Green
                const tierKeys = tier === 'Good' ? ['Good', 'Healthy'] : [tier];
                let defaultNoteTemplate = null;

                for (const tKey of tierKeys) {
                    const rules = threshold.rules?.[tKey] || {};
                    const defRules = threshold.default_rules?.[tKey] || {};

                    // Priority: 1. rules.note -> 2. rules.notes[0] -> 3. default_rules.note -> 4. default_rules.notes[0]
                    defaultNoteTemplate = rules.note || 
                                          (Array.isArray(rules.notes) && rules.notes[0]?.text) ||
                                          defRules.note || 
                                          (Array.isArray(defRules.notes) && defRules.notes[0]?.text);
                    
                    if (defaultNoteTemplate) break;
                }

                if (defaultNoteTemplate) {
                    finalDescription = applyVariables(defaultNoteTemplate, finalFinding);
                }
            }
        }

        finalFinding.description = finalDescription;

        if (override?.displayName) {
            finalFinding.title = override.displayName;
        } else if (cat.label) {
            finalFinding.title = cat.label;
        }

        return finalFinding;
    });

    // CRITICAL: Always show exactly 9 items (3x3 grid)
    const finalFindings = allFinalFindings.slice(0, 9);

    // 6. Calculate audit score (only for the visible 9 items)
    const calculateAuditScore = () => {
        const knownFindings = finalFindings.filter(f => f.status !== 'Unknown');
        if (knownFindings.length === 0) return 0;
        const scores = knownFindings.map(f => f.score);
        const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
        return Number.isInteger(avgScore) ? avgScore : parseFloat(avgScore.toFixed(2));
    };

    const auditScore = calculateAuditScore();


    return (
        <BaseScanGridWidget
            findings={finalFindings}
            title={title}
            auditScore={auditScore}
            onCardClick={handleCardClick}
            style={style}
            config={config}
            theme={theme}
        />
    );
};
